import React from "react";

import {
	ChatBetSlipSharedPopup,
	ImagePreviewPopup,
} from "@features/chat/desktop";
import { UserDataConfirmAuthPopup } from "@features/membership/user-data-confirm/shared";
import {
	BetBuilderPicksNotAvailablePopup,
	BetBuilderPicksPopup,
} from "@features/quick-play/desktop/bet-builder-picks";
import BettingTipsPopup from "@features/quick-play/desktop/betting-tips/BettingTipsPopup";
import MostPlayTicketsPopup from "@features/quick-play/desktop/most-played-tickets/MostPlayTicketsPopup";
import SharedBetSlipPopup from "@features/quick-play/desktop/shared-bet-slips/components/SharedBetSlipPopup";
import { useRootAppStore } from "@hooks";
import { ReactLazyImportWithGuard as lazy } from "@lib/lazy-import-with-guard";
import { Modal } from "@lib/modal";
import BlockPopupService from "@services/user/BlockPopupService";
import { getCurrentCulture } from "@utils";
import { observer } from "mobx-react-lite";

import {
	AccountVerificationPopup,
	AutomaticLogoutPopup,
	DenyUserMatchingConfirmationPopup,
	FailedLoginsPopup,
	OnboardingNextPopup,
	OnboardingRegisterPopup,
	RequiredUserActionsPopup,
	UseMarketplaceBonusConfirmPopup,
	User24hLockPopup,
	WelcomeLoginPopup,
	XtremepushPopup,
} from ".";
import { PopupName } from "../shared/PopupName";
import BetBuilderPopup from "./BetBuilderPopup";
import CountryChangePopup from "./CountryChangePopup";
import LiveSessionExpirePopup from "./LiveSessionExpirePopup";
import OnboardingNewsletterPopup from "./OnboardingNewsletterPopup";
import RealityCheckPopup from "./reality-check/RealityCheckPopup";

//#region lazy
const loadFailPath = `/${getCurrentCulture()}/app-update`;
//prettier-ignore
const LogoutPopup = lazy(loadFailPath, () => import("../shared/LogoutPopup"));
//prettier-ignore
const GracePeriodConfirmationPopup = lazy(loadFailPath, () => import("../shared/GracePeriodConfirmationPopup"));
//prettier-ignore
const AreaChangeConfirmationPopup = lazy(loadFailPath, () => import("../shared/AreaChangeConfirmationPopup"));
//prettier-ignore
const WalletGracePeriodDepositPopup = lazy(loadFailPath, () => import("../shared/WalletGracePeriodDepositPopup"));
//prettier-ignore
const SavedSlipInfoPopup = lazy(loadFailPath, () => import("@features/saved-slip/desktop/components/SavedSlipInfoPopup"));
//#endregion lazy

export default observer(function PopupDisplay() {
	return (
		<React.Suspense fallback={null}>
			<Modal>
				<Popup />
			</Modal>
		</React.Suspense>
	);
});

const Popup = observer(function Popup() {
	const rootStore = useRootAppStore();

	const {
		automaticLogoutStore: { isActive: isAutomaticLogoutActive },
		realityCheckStore: { isActive: isRealityCheckActive },
		user24hLockStore: { isActive: isUser24hLockActive },
		accountVerificationStore: { isActive: isAccountVerificationActive },
		welcomeStore: {
			isActive: isWelcomePopupActive,
			isUseBonusConfirmActive,
			hideUseBonusConfirmationPopup,
		},
		failedLoginsCheckPopupStore: { isActive: isFailedLoginsPopupActive },
		offerHub: { isSessionExpired: isOfferHubSessionExpire },
		cashoutHubStore: { isSessionExpired: isCashOutHubSessionExpire },
		userHubStore: { isSessionExpired: isUserHubSessionExpire },
		onboardingWelcomePopupStore: {
			onboardingNewsletterStore: {
				isActive: isOnboardingNewsletterPopupActive,
			},
			onboardingRegistrationStore: {
				isActive: isOnboardingRegisterPopupActive,
			},
			onboardingNextStore: { isActive: isOnboardingNextPopupActive },
		},
		requiredUserActionStore: { isActive: RequiredUserActionsPopupActive },
		quickPlayStore: {
			mostPlayedTicketsStore: {
				isPopupDisplayed: isQuickPlayPopupActive,
			},
			betBuilderPicksStore: {
				isPopupDisplayed: isBetBuilderPickPopupActive,
				BBPickNotAvailablePopup: isBBPickNoAvailablePopupActive,
			},
			sharedBetSlipStore: {
				isPopupDisplayed: isSharedBetSlipPopupActive,
			},
			bettingTipsStore: { isPopupDisplayed: isBettingTipsActive },
		},
		cookieConsentStore: {
			shouldBlockOtherPopups: isCookieConsentPopupActive,
		},
		chatViewStore: {
			chatBetSlipShareData: isChatBetSlipSharePopupActive,
			imagePreview,
		},
		userAuthStore,
		sportStatisticsHub: {
			isSessionExpired: isSportStatisticsHubSessionExpired,
		},
		betBoosterHubStore: { isSessionExpired: isBetBoosterHubSessionExpire },
		countryChangeStore: {
			isCountryChangePopupActive: isCountryChangePopupActive,
		},
		xtremepushNotificationPermissionPopupStore: {
			isActive: isNotificationsPopupActive,
		},
		gracePeriodStore: { isActive: isGracePeriodConfirmationActive },
		areaChangeStore: { isActive: isAreaChangeConfirmationActive },
		walletGracePeriodPopupStore: {
			isActive: isWalletGracePeriodPopupActive,
		},
		logoutPopupStore: { isLogoutPopupActive },
		betSlipStore: {
			savedSlipStore: {
				isSavedSlipInfoPopupActive: isSavedSlipInfoPopupActive,
			},
		},
	} = rootStore;

	if (
		(isAutomaticLogoutActive ||
			isRealityCheckActive ||
			isUser24hLockActive ||
			isAccountVerificationActive ||
			isWelcomePopupActive ||
			isFailedLoginsPopupActive ||
			isOfferHubSessionExpire ||
			isCashOutHubSessionExpire ||
			isOnboardingNewsletterPopupActive ||
			isOnboardingRegisterPopupActive ||
			RequiredUserActionsPopupActive ||
			isQuickPlayPopupActive ||
			isBBPickNoAvailablePopupActive ||
			isBetBuilderPickPopupActive ||
			isSharedBetSlipPopupActive ||
			isCookieConsentPopupActive ||
			isNotificationsPopupActive ||
			isBettingTipsActive) &&
		rootStore.chatViewStore.chatBoxVisible
	) {
		rootStore.chatViewStore.toggleChatBox(false);
	}

	if (isLogoutPopupActive) {
		return <LogoutPopup />;
	}

	if (
		isRealityCheckActive &&
		!BlockPopupService.checkIfPopupBlocked(PopupName.RealityCheck)
	) {
		return (
			<RealityCheckPopup isRealityCheckActive={isRealityCheckActive} />
		);
	}

	if (
		isAutomaticLogoutActive &&
		!BlockPopupService.checkIfPopupBlocked(PopupName.AutomaticLogout)
	) {
		return <AutomaticLogoutPopup isOpened={isAutomaticLogoutActive} />;
	}

	if (
		(isOfferHubSessionExpire ||
			isCashOutHubSessionExpire ||
			isSportStatisticsHubSessionExpired ||
			isBetBoosterHubSessionExpire ||
			isUserHubSessionExpire) &&
		!BlockPopupService.checkIfPopupBlocked(
			PopupName.CashoutExpire || PopupName.SessionExpire
		)
	) {
		return <LiveSessionExpirePopup />;
	}

	if (isBBPickNoAvailablePopupActive) {
		return <BetBuilderPicksNotAvailablePopup />;
	}

	if (isBetBuilderPickPopupActive) {
		return <BetBuilderPicksPopup />;
	}

	if (isSavedSlipInfoPopupActive) {
		return <SavedSlipInfoPopup />;
	}

	if (
		isQuickPlayPopupActive &&
		!BlockPopupService.checkIfPopupBlocked(PopupName.QuickPlay)
	) {
		return <MostPlayTicketsPopup />;
	}

	if (
		isSharedBetSlipPopupActive &&
		!BlockPopupService.checkIfPopupBlocked(PopupName.SharedBetSlip)
	) {
		return <SharedBetSlipPopup />;
	}

	if (
		isBettingTipsActive &&
		!BlockPopupService.checkIfPopupBlocked(PopupName.BettingTips)
	) {
		return (
			<BettingTipsPopup
				bettingTipsStore={rootStore.quickPlayStore.bettingTipsStore}
			/>
		);
	}

	if (
		rootStore.betBuilderPopupStore.isActive &&
		!BlockPopupService.checkIfPopupBlocked(PopupName.BetBuilder)
	) {
		return <BetBuilderPopup />;
	}
	if (
		isCookieConsentPopupActive &&
		!BlockPopupService.checkIfPopupBlocked(PopupName.CookieConsent)
	) {
		//do not render any popups if consent is shown
		return null;
	}

	if (
		isAccountVerificationActive &&
		!BlockPopupService.checkIfPopupBlocked(PopupName.AccountVerification)
	) {
		return (
			<AccountVerificationPopup isOpened={isAccountVerificationActive} />
		);
	}

	if (
		isUser24hLockActive &&
		!BlockPopupService.checkIfPopupBlocked(PopupName.User24LockActive)
	) {
		return <User24hLockPopup />;
	}

	if (isNotificationsPopupActive) {
		return <XtremepushPopup />;
	}

	if (
		isWelcomePopupActive &&
		!BlockPopupService.checkIfPopupBlocked(PopupName.WelcomePopup)
	) {
		return <WelcomeLoginPopup />;
	}

	if (
		isUseBonusConfirmActive &&
		!BlockPopupService.checkIfPopupBlocked(PopupName.UseBonus)
	) {
		return (
			<UseMarketplaceBonusConfirmPopup
				hidePopup={hideUseBonusConfirmationPopup}
				sendConfirmation={(value: boolean) =>
					userAuthStore.sendWelcomeUseBonusConfirmation(value)
				}
			/>
		);
	}

	if (
		isFailedLoginsPopupActive &&
		!BlockPopupService.checkIfPopupBlocked(PopupName.FailedLogins)
	) {
		return <FailedLoginsPopup />;
	}

	if (
		isOnboardingNextPopupActive &&
		!BlockPopupService.checkIfPopupBlocked(PopupName.OnboardingNext)
	) {
		return (
			<OnboardingNextPopup
				isOnboardingNextPopupActive={isOnboardingNextPopupActive}
			/>
		);
	}

	if (
		isOnboardingRegisterPopupActive &&
		!BlockPopupService.checkIfPopupBlocked(PopupName.OnboardingRegister)
	) {
		return (
			<OnboardingRegisterPopup
				isOnboardingRegisterPopupActive={
					isOnboardingRegisterPopupActive
				}
			/>
		);
	}

	if (
		isOnboardingNewsletterPopupActive &&
		!BlockPopupService.checkIfPopupBlocked(PopupName.OnboardingNewsletter)
	) {
		return (
			<OnboardingNewsletterPopup
				isOnboardingNewsletterPopupActive={
					isOnboardingNewsletterPopupActive
				}
			/>
		);
	}

	if (
		RequiredUserActionsPopupActive &&
		!BlockPopupService.checkIfPopupBlocked(PopupName.RequiredUserActions)
	) {
		if (userAuthStore.user) {
			return <RequiredUserActionsPopup />;
		}
	}

	if (
		isChatBetSlipSharePopupActive &&
		!BlockPopupService.checkIfPopupBlocked(PopupName.ChatBetSlipShare)
	) {
		return <ChatBetSlipSharedPopup />;
	}

	if (isCountryChangePopupActive) {
		return <CountryChangePopup />;
	}

	if (imagePreview) {
		return <ImagePreviewPopup />;
	}

	if (isWalletGracePeriodPopupActive) {
		return <WalletGracePeriodDepositPopup />;
	}

	if (isGracePeriodConfirmationActive) {
		return <GracePeriodConfirmationPopup />;
	}

	if (isAreaChangeConfirmationActive) {
		return <AreaChangeConfirmationPopup />;
	}

	if (
		userAuthStore.userDataConfirmationStore
			.isUserDataConfirmationPopupActive
	) {
		return <UserDataConfirmAuthPopup />;
	}

	if (userAuthStore.userMatchingStore.isDenyConfirmationPopupActive) {
		return <DenyUserMatchingConfirmationPopup />;
	}

	return null;
});
